/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: UserFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for User Access tab.
────────────────────────────────────────────────────────────────*/

import { Users, User, Search } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import LanguageSelect from '../../../components/ui/LanguageSelect';
import { startupDialogOptions } from '../../../data/formOptions';
import type { UserFormState } from '../hooks/useTenantData';

interface UserFormFieldsProps {
  userData: UserFormState;
  useCurrentUser: boolean;
  isCreatingNewUser: boolean;
  selectedUserOption: 'search' | 'create' | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: keyof UserFormState, value: any) => void;
  onUseCurrentUserChange: (value: boolean) => void;
  onSelectCreateNew: () => void;
  onSelectSearch: () => void;
}

export function UserFormFields({
  userData,
  useCurrentUser,
  isCreatingNewUser,
  selectedUserOption,
  onInputChange,
  onUseCurrentUserChange,
  onSelectCreateNew,
  onSelectSearch
}: UserFormFieldsProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('tenants.tabs.user')} icon={<Users />}>
      <div className="mb-4">
        <p className="text-sm text-muted-foreground">{t('tenants.userAccess.description')}</p>
      </div>

      {/* User Selection Options */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        {/* Use Current User - 2 columns */}
        <div className="col-span-2 rounded-lg border border-border bg-muted/50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.useCurrentUser')}
              </label>
              <p className="text-xs text-muted-foreground">
                {t('tenants.userAccess.useCurrentUserHint')}
              </p>
            </div>
            <Toggle
              value={useCurrentUser}
              onChange={onUseCurrentUserChange}
            />
          </div>
        </div>

        {/* User Options - 2 columns */}
        {!useCurrentUser && (
          <>
            <div className="col-span-1">
              <button
                type="button"
                onClick={onSelectCreateNew}
                className={`flex w-full items-center justify-center gap-2 rounded px-4 py-3 text-sm transition-colors ${
                  selectedUserOption === 'create'
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                }`}
              >
                <User className="h-4 w-4" />
                {t('tenants.userAccess.buttons.createNew')}
              </button>
            </div>
            <div className="col-span-1">
              <button
                type="button"
                onClick={onSelectSearch}
                className={`flex w-full items-center justify-center gap-2 rounded px-4 py-3 text-sm transition-colors ${
                  selectedUserOption === 'search'
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                }`}
              >
                <Search className="h-4 w-4" />
                {t('tenants.userAccess.buttons.searchUser')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* User Form - Only show when not using current user */}
      {!useCurrentUser && (
        <div>
          {/* User Status Indicator */}
          <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              {isCreatingNewUser ? (
                <>
                  <User className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-blue-600">
                    {t('tenants.userAccess.status.creatingTitle')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t('tenants.userAccess.status.creatingHint')}
                  </span>
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600">
                    {t('tenants.userAccess.status.existingTitle')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t('tenants.userAccess.status.existingHint')}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            {/* Row 1 - Username and Email */}
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.username')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.username')} *
              </label>
            </SimpleTooltip>
            <input
              type="text"
              value={userData.username}
              onChange={(e) => onInputChange('username', e.target.value)}
              placeholder={t('tenants.userAccess.placeholders.username')}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.email')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.email')} *
              </label>
            </SimpleTooltip>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              placeholder={t('tenants.userAccess.placeholders.email')}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />

            {/* Row 2 - Password and Full Name */}
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.password')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.password')} *
              </label>
            </SimpleTooltip>
            <input
              type="password"
              value={userData.password}
              onChange={(e) => onInputChange('password', e.target.value)}
              placeholder={t('tenants.userAccess.placeholders.password')}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.fullName')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.fullName')}
              </label>
            </SimpleTooltip>
            <input
              type="text"
              value={userData.fullName}
              onChange={(e) => onInputChange('fullName', e.target.value)}
              placeholder={t('tenants.userAccess.placeholders.fullName')}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />

            {/* Row 3 - Department and Locale */}
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.department')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.department')}
              </label>
            </SimpleTooltip>
            <input
              type="text"
              value={userData.department || ''}
              onChange={(e) => onInputChange('department', e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
              placeholder={t('tenants.userAccess.placeholders.department')}
            />
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.locale')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.locale')}
              </label>
            </SimpleTooltip>
            <LanguageSelect
              value={userData.locale}
              onChange={(value) => onInputChange('locale', value)}
              placeholder={t('tenants.userAccess.placeholders.locale')}
            />

            {/* Row 4 - Timezone and Startup Dialog */}
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.timezone')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.timezone')}
              </label>
            </SimpleTooltip>
            <TimezoneSelect
              value={userData.timezone}
              onChange={(value) => onInputChange('timezone', value)}
              placeholder={t('tenants.userAccess.placeholders.timezone')}
            />
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.startupDialog')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.startupDialog')}
              </label>
            </SimpleTooltip>
            <select
              value={userData.startupDialog}
              onChange={(e) => onInputChange('startupDialog', e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            >
              {startupDialogOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {/* Row 5 - TOTP and Active */}
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.totp')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.totpEnabled')}
              </label>
            </SimpleTooltip>
            <Toggle
              value={userData.totpEnabled}
              onChange={(value) => onInputChange('totpEnabled', value)}
            />
            <SimpleTooltip
              content={t('tenants.userAccess.tooltips.active')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('tenants.userAccess.fields.active')}
              </label>
            </SimpleTooltip>
            <Toggle
              value={userData.isActive}
              onChange={(value) => onInputChange('isActive', value)}
            />
          </div>
        </div>
      )}
    </FormBox>
  );
}

